import { AODSvgExport } from "@/src/svg/admissionOfficeDatabase/AODSvgExport";
import { SIDEBAR_SITE_OPTIONS } from "@/src/constants/accessControl";
import { formatOfficeUrlForDisplay, officeUrlsMatch } from "@/src/utils/accessControl/formatOfficeUrl";

const defaultWidth = 5;

export const admissionDatabaseActionsConst = [
  { name: "prev", icon: AODSvgExport.AODPrevDocSvg },
  { name: "comment", icon: AODSvgExport.AODCommentDocSvg },
  { name: "download", icon: AODSvgExport.AODDownloadDocSvg },
  { name: "send-email", icon: AODSvgExport.AODSendEmailSvg },
  { name: "delete", icon: AODSvgExport.AODDeleteDocSvg },
];

export const admissionDatabaseIsAvailableConst = [
  { id: "true", label: "true" },
  { id: "false", label: "false" },
] as const;

export const resolveAdmissionDatabaseIsAvailableId = (value?: boolean | null): "true" | "false" => {
  return value === false ? "false" : "true";
};

export const resolveAdmissionDatabaseIsAvailableLabel = (value?: boolean | null): string => {
  return value === false ? "false" : "true";
};

export const admissionDatabaseOfficeUrlConst = [
  { id: "null", label: "null", value: null as string | null },
  ...SIDEBAR_SITE_OPTIONS.map((url) => ({
    id: url,
    label: formatOfficeUrlForDisplay(url),
    value: url,
  })),
] as const;

export const admissionDatabaseFormOfficeConst = [
  { id: "null", label: "null", value: null as string | null },
  { id: "Malta", label: "MALTA", value: "Malta" },
  { id: "Poland", label: "POLAND", value: "Poland" },
  { id: "Ukraine", label: "UKRAINE", value: "Ukraine" },
] as const;

export const resolveAdmissionDatabaseOfficeUrlId = (value?: string | null): string => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "null";
  return (
    admissionDatabaseOfficeUrlConst.find((item) => item.value != null && officeUrlsMatch(item.value, trimmed))?.id ??
    "null"
  );
};

export const resolveAdmissionDatabaseOfficeUrlLabel = (value?: string | null): string => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "null";
  return (
    admissionDatabaseOfficeUrlConst.find((item) => item.value != null && officeUrlsMatch(item.value, trimmed))?.label ??
    formatOfficeUrlForDisplay(trimmed)
  );
};

export const resolveAdmissionDatabaseFormOfficeId = (value?: string | null): string => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "null";
  const matched = admissionDatabaseFormOfficeConst.find(
    (item) => item.value != null && item.value.toLowerCase() === trimmed.toLowerCase(),
  );
  return matched?.id ?? "null";
};

export const resolveAdmissionDatabaseFormOfficeLabel = (value?: string | null): string => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "null";
  const matched = admissionDatabaseFormOfficeConst.find(
    (item) => item.value != null && item.value.toLowerCase() === trimmed.toLowerCase(),
  );
  return matched?.label ?? trimmed.toUpperCase();
};

export const admissionDatabaseAllFieldsList = [
  { title: "Date", id: "data_added", width: defaultWidth },
  { title: "Full Name", id: "full_name", width: defaultWidth },
  { title: "Title", id: "title", width: defaultWidth },
  { title: "Type", id: "document_type", width: defaultWidth },
  { title: "Seria", id: "document_seria", width: defaultWidth },
  { title: "Data of Birth", id: "date_of_birth", width: defaultWidth },
  { title: "Nationality", id: "nationality", width: defaultWidth },
  { title: "Course", id: "course", width: defaultWidth },
  { title: "Whose", id: "whose_doc", width: defaultWidth },
  { title: "Office Url", id: "office_url", width: defaultWidth },
  { title: "Form Office", id: "form_office", width: defaultWidth },
  { title: "Is Available", id: "is_available", width: defaultWidth },
  { title: "Current Document", id: "current_main_doc", width: 8 },
];

export const admissionDatabaseDefaultConfiguration = [
  { title: "Date", id: "data_added", width: 5.5 },
  { title: "Full Name", id: "full_name", width: 14 },
  { title: "Title", id: "title", width: 6.875 },
  { title: "Type", id: "document_type", width: 6 },
  { title: "Seria", id: "document_seria", width: 6.313 },
  { title: "Data of Birth", id: "date_of_birth", width: 5.5 },
  { title: "Nationality", id: "nationality", width: 6.5 },
  { title: "Course", id: "course", width: 11.625 },
  { title: "Whose", id: "whose_doc", width: 5.875 },
  { title: "Office Url", id: "office_url", width: 9 },
  { title: "Form Office", id: "form_office", width: 6.25 },
  { title: "Is Available", id: "is_available", width: 5.5 },
  { title: "Current Document", id: "current_main_doc", width: 8 },
  // { title: "Actions", id: "document_actions", width: 9 },
];

export const admissionDatabaseDefaultBodyFilters = {
  page: 1,
  query: "",
  start: "",
  end: "",
  types: [],
  nationalities: [],
  courses: [],
  whose_docs: [],
  titles: [],
};

type AODColumnConfig = { title: string; id: string; width?: number };

export function normalizeVisibleAODColumns(columns: AODColumnConfig[]): AODColumnConfig[] {
  const knownIds = new Set(admissionDatabaseAllFieldsList.map((col) => col.id));
  let next = columns
    .map((col) => (col.id === "is_main" ? { ...col, id: "current_main_doc", title: "Current Document" } : col))
    .filter((col) => knownIds.has(col.id))
    .map((col) => {
      const canonical = admissionDatabaseAllFieldsList.find((c) => c.id === col.id);
      return { ...canonical!, width: col.width ?? canonical?.width ?? defaultWidth };
    });

  const ensureAfterWhose = (columnId: string) => {
    if (next.some((col) => col.id === columnId)) return;
    const column = admissionDatabaseAllFieldsList.find((c) => c.id === columnId);
    if (!column) return;

    const whoseIndex = next.findIndex((c) => c.id === "whose_doc");
    const officeUrlIndex = next.findIndex((c) => c.id === "office_url");
    const formOfficeIndex = next.findIndex((c) => c.id === "form_office");

    let insertAt = whoseIndex >= 0 ? whoseIndex + 1 : next.length;
    if (columnId === "form_office" && officeUrlIndex >= 0) {
      insertAt = officeUrlIndex + 1;
    } else if (columnId === "is_available") {
      if (formOfficeIndex >= 0) insertAt = formOfficeIndex + 1;
      else if (officeUrlIndex >= 0) insertAt = officeUrlIndex + 1;
    } else if (columnId === "current_main_doc") {
      const isAvailableIndex = next.findIndex((c) => c.id === "is_available");
      if (isAvailableIndex >= 0) insertAt = isAvailableIndex + 1;
      else if (formOfficeIndex >= 0) insertAt = formOfficeIndex + 1;
      else if (officeUrlIndex >= 0) insertAt = officeUrlIndex + 1;
    }

    next.splice(insertAt, 0, { ...column });
  };

  ensureAfterWhose("office_url");
  ensureAfterWhose("form_office");
  ensureAfterWhose("is_available");
  ensureAfterWhose("current_main_doc");

  return next;
}
